import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Paper, Rating, Stack, Tab, Tabs, Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import api from "../../api";
import LoadError from "../../components/admin/LoadError";
import TestimonialsManage from "./TestimonialsManage";
import { adminHasPermission } from "../../utils/adminPermissions";

const TABS = ["pending", "approved", "rejected"];

/** Super Admin always gets full testimonial controls (the API allows it too). */
function isSuperAdminUser() {
  try {
    const roles = [localStorage.getItem("role"), localStorage.getItem("admin-role-name")];
    const raw = localStorage.getItem("userData");
    if (raw) {
      const u = JSON.parse(raw);
      roles.push(u?.portal_role, u?.role);
    }
    return roles.some((r) => ["super admin", "super_admin"].includes(String(r || "").toLowerCase().trim()));
  } catch {
    return false;
  }
}

export default function Testimonials() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [canManage, setCanManage] = useState(
    () => isSuperAdminUser() || adminHasPermission("manage-testimonials")
  );

  // Re-read permissions from the API: the copy in localStorage can be stale right after a
  // deploy that adds new permissions (it is only refreshed at login / by the layout later).
  useEffect(() => {
    let cancelled = false;
    api
      .get("admin/auth/permissions")
      .then((res) => {
        const perms = res.data?.data?.permissions;
        const role = String(res.data?.data?.role || "").toLowerCase().trim();
        if (cancelled || !Array.isArray(perms)) return;
        setCanManage(
          role === "super admin" || role === "super_admin" || isSuperAdminUser() || perms.includes("manage-testimonials")
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    if (status === "manage") return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api.get(`admin/testimonials?status=${status}`);
      setRows(res.data?.data || []);
    } catch (err) {
      setRows([]);
      setLoadError(err);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const setRowStatus = async (row, next) => {
    try {
      await api.patch(`admin/testimonials/${row.id}`, { status: next });
      toast.success(next === "approved" ? "Approved - now live on the home page" : "Updated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not update");
    }
  };

  const remove = async (row) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await api.delete(`admin/testimonials/${row.id}`);
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700 }}>
        Dashboard /{" "}
        <Box component="span" sx={{ color: "#3B2A9F", fontWeight: 600 }}>
          Testimonials
        </Box>
      </Typography>
      <Typography variant="h5" fontWeight={700} mt={0.5} mb={0.5}>
        Testimonials
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Reviews users send from the app, plus ones you add yourself. Only approved, consented and active ones show on the home page.
      </Typography>

      <Tabs value={status} onChange={(_, v) => setStatus(v)} sx={{ mb: 2 }}>
        {TABS.map((t) => (
          <Tab key={t} value={t} label={t[0].toUpperCase() + t.slice(1)} />
        ))}
        <Tab value="manage" label="Manage" />
      </Tabs>

      {status === "manage" ? (
        <TestimonialsManage canManage={canManage} />
      ) : loading ? (
        <CircularProgress />
      ) : loadError ? (
        <LoadError what="testimonials" error={loadError} onRetry={load} />
      ) : rows.length === 0 ? (
        <Typography color="text.secondary">No {status} testimonials yet.</Typography>
      ) : (
        <Stack spacing={1.5}>
          {rows.map((row) => (
            <Paper key={row.id} sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                <Rating value={row.rating} readOnly size="small" />
                <Typography fontWeight={700}>{row.display_name}</Typography>
                {row.child_year && <Chip size="small" label={row.child_year} />}
                {row.exam_board && <Chip size="small" label={row.exam_board} />}
              </Stack>
              <Typography sx={{ mb: 1.5 }}>{row.quote}</Typography>
              {canManage && (
              <Stack direction="row" spacing={1}>
                {status !== "approved" && (
                  <Button size="small" variant="contained" onClick={() => setRowStatus(row, "approved")}>
                    Approve
                  </Button>
                )}
                {status !== "rejected" && (
                  <Button size="small" variant="outlined" onClick={() => setRowStatus(row, "rejected")}>
                    {status === "approved" ? "Hide" : "Reject"}
                  </Button>
                )}
                <Button size="small" color="error" onClick={() => remove(row)}>
                  Delete
                </Button>
              </Stack>
              )}
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
}
